import { ApiError, expectOk, unwrap, type Schemas } from '@swarm/api-client';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { api } from '../../lib/api';

export type LocationListItem = Schemas['LocationListItemDto'];
export type Location = Schemas['LocationDto'];
export type SaveLocationRequest = Schemas['SaveLocationRequest'];
export type LocationKind = Schemas['LocationKindDto'];
export type ColonyListItem = Schemas['ColonyListItemDto'];
export type Colony = Schemas['ColonyDto'];
export type ColonyStatus = Schemas['ColonyStatusDto'];
export type ColonyOrigin = Schemas['ColonyOriginDto'];
export type DatePrecision = Schemas['DatePrecisionDto'];
export type SaveColonyRequest = Schemas['SaveColonyRequest'];
export type TimelineEntry = Schemas['TimelineEntryDto'];
export type FamilyMember = Schemas['FamilyMemberDto'];
export type Treatment = Schemas['TreatmentDto'];
export type TreatmentFields = Schemas['TreatmentFields'];
export type TreatmentApplication = Schemas['TreatmentApplicationDto'];
export type Feeding = Schemas['FeedingDto'];
export type FeedingFields = Schemas['FeedingFields'];
export type FeedUnit = Schemas['FeedUnitDto'];
export type FeedingPurpose = Schemas['FeedingPurposeDto'];
export type MiteCount = Schemas['MiteCountDto'];
export type MiteCountRequest = Schemas['MiteCountRequest'];
export type MiteCountMethod = Schemas['MiteCountMethodDto'];
export type Note = Schemas['NoteDto'];
export type TreatmentMethod = Schemas['TreatmentMethodDto'];
export type SaveTreatmentMethodRequest = Schemas['SaveTreatmentMethodRequest'];
export type FeedType = Schemas['FeedTypeDto'];
export type SaveFeedTypeRequest = Schemas['SaveFeedTypeRequest'];
export type FeedingSummary = Schemas['FeedingSummaryDto'];
export type GeocodeResult = Schemas['GeocodeResultDto'];
export type Infestation = Schemas['InfestationDto'];

export type StatusFilter = 'active' | 'inactive' | 'all';

export interface RecordFilter {
  colony_id?: string | undefined;
  location_id?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  method_id?: string | undefined;
  running?: boolean | undefined;
  /** Column sort on the server (#188): `date`, `-date`, … */
  sort?: string | undefined;
}

const keys = {
  all: ['apiary'] as const,
  locations: (archived: boolean) => ['apiary', 'locations', archived] as const,
  location: (id: string) => ['apiary', 'location', id] as const,
  colonies: (f: object) => ['apiary', 'colonies', f] as const,
  colony: (id: string) => ['apiary', 'colony', id] as const,
  timeline: (id: string) => ['apiary', 'colony', id, 'timeline'] as const,
  family: (id: string) => ['apiary', 'colony', id, 'family'] as const,
  feedingSummary: (id: string, year: number) => ['apiary', 'colony', id, 'feeding', year] as const,
  records: (kind: string, f: RecordFilter) => ['apiary', kind, f] as const,
  methods: (archived: boolean) => ['apiary', 'methods', archived] as const,
  feedTypes: (archived: boolean) => ['apiary', 'feed-types', archived] as const,
};

/** Any apiary change can affect lists, details and timelines. */
function invalidate(client: QueryClient) {
  return client.invalidateQueries({ queryKey: keys.all });
}

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

/** Photos are served by the API; the version busts the browser cache. */
export function photoUrl(path: string, version: number): string {
  return `/api/v1${path}?v=${version}`;
}

// --- Locations ----------------------------------------------------------------

export function useLocations(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: keys.locations(includeArchived),
    queryFn: async () =>
      unwrap(
        await api.GET('/locations', { params: { query: { include_archived: includeArchived } } }),
      ),
    enabled,
  });
}

export function useLocation(id: string) {
  return useQuery({
    queryKey: keys.location(id),
    queryFn: async () =>
      unwrap(await api.GET('/locations/{location_id}', { params: { path: { location_id: id } } })),
  });
}

export function useSaveLocation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id?: string | undefined;
      body: SaveLocationRequest;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/locations/{location_id}', {
              params: { path: { location_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/locations', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useArchiveLocation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        archived
          ? await api.POST('/locations/{location_id}/archive', {
              params: { path: { location_id: id } },
            })
          : await api.POST('/locations/{location_id}/unarchive', {
              params: { path: { location_id: id } },
            }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useGeocode() {
  return useMutation({
    mutationFn: async (q: string) =>
      unwrap(await api.GET('/geocode', { params: { query: { q } } })),
  });
}

// --- Colonies -----------------------------------------------------------------

export function useColonies(
  filter: { status?: StatusFilter; location_id?: string | undefined; q?: string | undefined } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: keys.colonies(filter),
    queryFn: async () =>
      unwrap(
        await api.GET('/colonies', {
          params: {
            query: {
              status: filter.status ?? 'active',
              ...(filter.location_id ? { location_id: filter.location_id } : {}),
              ...(filter.q ? { q: filter.q } : {}),
            },
          },
        }),
      ),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useColony(id: string) {
  return useQuery({
    queryKey: keys.colony(id),
    queryFn: async () =>
      unwrap(await api.GET('/colonies/{colony_id}', { params: { path: { colony_id: id } } })),
  });
}

/** Oldest known ancestor and all its splits and swarms (VOL-009). */
export function useColonyFamily(id: string) {
  return useQuery({
    queryKey: keys.family(id),
    queryFn: async () =>
      unwrap(
        await api.GET('/colonies/{colony_id}/family', { params: { path: { colony_id: id } } }),
      ),
  });
}

export function useTimeline(id: string) {
  return useQuery({
    queryKey: keys.timeline(id),
    queryFn: async () =>
      unwrap(
        await api.GET('/colonies/{colony_id}/timeline', { params: { path: { colony_id: id } } }),
      ),
  });
}

export function useFeedingSummary(id: string, year: number, enabled = true) {
  return useQuery({
    queryKey: keys.feedingSummary(id, year),
    queryFn: async () =>
      unwrap(
        await api.GET('/colonies/{colony_id}/feeding-summary', {
          params: { path: { colony_id: id }, query: { year } },
        }),
      ),
    enabled,
  });
}

export function useCreateColony() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveColonyRequest & { location_id: string }) =>
      unwrap(await api.POST('/colonies', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateColony() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id: string;
      body: SaveColonyRequest;
      version: number;
    }) =>
      unwrap(
        await api.PUT('/colonies/{colony_id}', {
          params: { path: { colony_id: id } },
          body,
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useChangeStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, on }: { id: string; status: ColonyStatus; on: string }) =>
      unwrap(
        await api.POST('/colonies/{colony_id}/status', {
          params: { path: { colony_id: id } },
          body: { status, on },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteColony() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      expectOk(await api.DELETE('/colonies/{colony_id}', { params: { path: { colony_id: id } } })),
    onSuccess: () => invalidate(client),
  });
}

export function useMoveColonies() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { colony_ids: string[]; location_id: string; on: string }) =>
      unwrap(await api.POST('/colonies/move', { body })),
    onSuccess: () => invalidate(client),
  });
}

/** Uploads (file) or removes (null) the photo of a colony, location or note. */
export function usePhoto(kind: 'colonies' | 'locations' | 'colony-notes') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File | null }) => {
      const response = await fetch(`/api/v1/${kind}/${id}/photo`, {
        method: file ? 'PUT' : 'DELETE',
        credentials: 'include',
        headers: {
          'X-Swarm-Csrf': '1',
          'Idempotency-Key': crypto.randomUUID(),
          ...(file ? { 'Content-Type': file.type || 'image/jpeg' } : {}),
        },
        body: file ?? null,
      });
      if (!response.ok) {
        throw new ApiError(response.status, await response.json().catch(() => undefined));
      }
      return (await response.json()) as unknown;
    },
    onSuccess: () => invalidate(client),
  });
}

// --- Notes ----------------------------------------------------------------------

export function useSaveNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      colonyId,
      id,
      body,
    }: {
      colonyId: string;
      id?: string | undefined;
      body: { noted_on: string; text: string };
    }) =>
      id
        ? unwrap(
            await api.PUT('/colony-notes/{note_id}', { params: { path: { note_id: id } }, body }),
          )
        : unwrap(
            await api.POST('/colonies/{colony_id}/notes', {
              params: { path: { colony_id: colonyId } },
              body,
            }),
          ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      expectOk(await api.DELETE('/colony-notes/{note_id}', { params: { path: { note_id: id } } })),
    onSuccess: () => invalidate(client),
  });
}

// --- Treatments, mite counts, feedings ----------------------------------------------

function recordQuery(filter: RecordFilter, cursor?: string) {
  return {
    ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== undefined && v !== '')),
    ...(cursor ? { cursor } : {}),
    limit: 50,
  };
}

export function useTreatments(filter: RecordFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.records('treatments', filter),
    queryFn: async ({ pageParam }) =>
      unwrap(await api.GET('/treatments', { params: { query: recordQuery(filter, pageParam) } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export function useRecordTreatment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: TreatmentFields & { colony_ids: string[] }) =>
      unwrap(await api.POST('/treatments', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateTreatment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id: string;
      body: TreatmentFields;
      version: number;
    }) =>
      unwrap(
        await api.PUT('/treatments/{treatment_id}', {
          params: { path: { treatment_id: id } },
          body,
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useEndTreatment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, on }: { id: string; on: string }) =>
      unwrap(
        await api.POST('/treatments/{treatment_id}/end', {
          params: { path: { treatment_id: id } },
          body: { on },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteTreatment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      expectOk(
        await api.DELETE('/treatments/{treatment_id}', { params: { path: { treatment_id: id } } }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useMiteCounts(filter: RecordFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.records('mite-counts', filter),
    queryFn: async ({ pageParam }) =>
      unwrap(await api.GET('/mite-counts', { params: { query: recordQuery(filter, pageParam) } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export function useSaveMiteCount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      colonyId,
      id,
      body,
      version,
    }: {
      colonyId: string;
      id?: string | undefined;
      body: MiteCountRequest;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/mite-counts/{mite_count_id}', {
              params: { path: { mite_count_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(
            await api.POST('/colonies/{colony_id}/mite-counts', {
              params: { path: { colony_id: colonyId } },
              body,
            }),
          ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteMiteCount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      expectOk(
        await api.DELETE('/mite-counts/{mite_count_id}', {
          params: { path: { mite_count_id: id } },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useFeedings(filter: RecordFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.records('feedings', filter),
    queryFn: async ({ pageParam }) =>
      unwrap(await api.GET('/feedings', { params: { query: recordQuery(filter, pageParam) } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export function useRecordFeeding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: FeedingFields & { colony_ids: string[]; amount_is_total?: boolean }) =>
      unwrap(await api.POST('/feedings', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateFeeding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id: string;
      body: FeedingFields;
      version: number;
    }) =>
      unwrap(
        await api.PUT('/feedings/{feeding_id}', {
          params: { path: { feeding_id: id } },
          body,
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteFeeding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      expectOk(
        await api.DELETE('/feedings/{feeding_id}', { params: { path: { feeding_id: id } } }),
      ),
    onSuccess: () => invalidate(client),
  });
}

// --- Catalogs ---------------------------------------------------------------------

export function useTreatmentMethods(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: keys.methods(includeArchived),
    queryFn: async () =>
      unwrap(
        await api.GET('/treatment-methods', {
          params: { query: { include_archived: includeArchived } },
        }),
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSaveTreatmentMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id?: string | undefined;
      body: SaveTreatmentMethodRequest;
    }) =>
      id
        ? unwrap(
            await api.PUT('/treatment-methods/{method_id}', {
              params: { path: { method_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST('/treatment-methods', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useArchiveTreatmentMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        await api.PUT('/treatment-methods/{method_id}/archived', {
          params: { path: { method_id: id } },
          body: { archived },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useFeedTypes(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: keys.feedTypes(includeArchived),
    queryFn: async () =>
      unwrap(
        await api.GET('/feed-types', { params: { query: { include_archived: includeArchived } } }),
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSaveFeedType() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: string | undefined; body: SaveFeedTypeRequest }) =>
      id
        ? unwrap(
            await api.PUT('/feed-types/{feed_type_id}', {
              params: { path: { feed_type_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST('/feed-types', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useArchiveFeedType() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        await api.PUT('/feed-types/{feed_type_id}/archived', {
          params: { path: { feed_type_id: id } },
          body: { archived },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}
